import {
  createColumn,
  formatAggregatedFooter,
  formatNumericCell,
  formatPriceCell,
  getCommonBodyCellPropsTradesPositions,
  getCommonFooterCellProps,
  getCommonHeadCellProps,
} from "./commonUtils";
import { formatPosition } from "../../../utils/dataCalculations";

export const columns = (theme, globalFilter, formatCurrency, formatNumber) => {
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const textStr = String(text);
    const searchStr = String(searchTerm);
    const regex = new RegExp(`(${searchStr})`, "gi");
    const parts = textStr.split(regex);
    return parts?.map((part, index) =>
      part.toLowerCase() === searchStr.toLowerCase() ? (
        <span
          key={index}
          style={{
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText,
            padding: "1px 2px",
            borderRadius: theme.shape.borderRadius * 0.5,
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellPropsTradesPositions(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyType",
      "Strategy Type",
      150,
      "left",
      {
        other: {
          Cell: ({ cell, table }) =>
            highlightMatch(cell.getValue(), table.options.state.globalFilter),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "Strategy Name",
      220,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "";
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "SYMBOL",
      "Symbol",
      220,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security Name",
      220,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell, table }) => {
            const value = cell.getValue();
            return value && value !== "nan"
              ? highlightMatch(value, table.options.state.globalFilter)
              : "-";
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "realtime_quantity",
      "Real-time Qty",
      140,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_trading_quantity",
      "Net Trade Today",
      140,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_price",
      "Current Price",
      140,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue()),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sod_quantity",
      "SOD Quantity",
      140,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB, columnId) => {
            const aValue = rowA.getValue(columnId);
            const bValue = rowB.getValue(columnId);
            if (!aValue || aValue === "nan") return 1;
            if (!bValue || bValue === "nan") return -1;
            const a = aValue || 0;
            const b = bValue || 0;
            return a - b;
          },
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "last_closing_price",
      "Last Close",
      140,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue()),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "LMV",
      "LMV",
      140,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB, columnId) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA > 0 ? marketValueA : 0;
            const valueB = marketValueB > 0 ? marketValueB : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue > 0 ? marketValue : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue > 0 ? marketValue : 0;
            return formatNumericCell(
              value,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            );
          },
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue > 0 ? marketValue : 0);
              }, 0);
            return formatNumericCell(
              total,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "SMV",
      "SMV",
      140,
      "right",
      {
        body: {
          color: theme.palette.error.main,
        },
        other: {
          sortingFn: (rowA, rowB, columnId) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA < 0 ? Math.abs(marketValueA) : 0;
            const valueB = marketValueB < 0 ? Math.abs(marketValueB) : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue < 0 ? Math.abs(marketValue) : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue < 0 ? Math.abs(marketValue) : 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue < 0 ? Math.abs(marketValue) : 0);
              }, 0);
            if (total === 0) return "-";
            const formattedTotal = `(${formatCurrency(total).replace(
              "$",
              ""
            )})`;
            return (
              <span
                style={{
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "market_value",
      "Market Value",
      140,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "market_value",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "total_pnl",
      "Total P&L",
      140,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "total_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "trading_pnl",
      "Trading P&L",
      140,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "trading_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_source",
      "Pricing Source",
      140,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "Security_Type",
      "Security Type",
      140,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "currency",
      "Currency",
      140,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue(),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "settle_currency",
      "Settle Currency",
      140,
      "right",
      {
        other: {
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value && value !== "nan" ? value : "-";
          },
        },
      }
    ),
  ];
};
